import type React from 'react'

import {
  ListingEnums,
  SearchEnums,
  ShipmentEnums,
} from '@b-stock/fe-core/locale'
import type {
  ItemCategoriesDtoCategoriesEnum,
  ListingDtoListingTypeEnum,
  ShippingTransportModeEnum,
} from '@b-stock/listing-api-client'
import type {
  BuyingFormatEnum,
  InventoryTypeEnum,
} from '@b-stock/search-api-client'
import type { ShipmentType } from '@b-stock/shipment-api-client'

import type { ListingFiltersState } from '@/components/listings/search/ListingSearchProvider'
import type { KeysMatching } from '@/helpers/types/KeysMatching'

export default {
  // Unlocalized strings
  productType: (option: string) => option,
  manufacturer: (option: string) => option,
  model: (option: string) => option,
  capacity: (option: string) => option,
  memory: (option: string) => option,
  carrier: (option: string) => option,
  sellerGrade: (option: string) => option,
  deals: (option: string) => option,
  allowedShipmentTypes: (option: string) => (
    <ShipmentEnums.ShipmentType.Component
      // We know the option will be an enum value in practice, even though
      // the API doesn't strictly enforce it. If it ever isn't, ListingEnums
      // should gracefully handle this and just output the raw option string.
      value={option as ShipmentType}
    />
  ),
  buyingFormat: (option: string) => (
    <SearchEnums.BuyingFormat.Component
      // We know the option will be an enum value in practice, even though
      // the API doesn't strictly enforce it. If it ever isn't, ListingEnums
      // should gracefully handle this and just output the raw option string.
      value={option as BuyingFormatEnum}
    />
  ),
  // Strictly speaking, `category` is localized subcategory data, but the localization
  // happens early, so the search service is acting on already localized values.
  category: (option: string) => option,
  categories: (option: string) => {
    const categoryDisplayNames: {
      [key in string]?: string
    } = {
      'Apparel Shoes & Accessories': 'Apparel, Shoes & Accessories',
      'Books Movies & Music': 'Books, Movies & Music',
      'Toys Kids & Baby': 'Toys, Kids & Baby',
    }
    if (categoryDisplayNames[option]) {
      return <span>{categoryDisplayNames[option]}</span>
    }

    return (
      <ListingEnums.Category.Component
        // The option should be a pre-localized value in practice. If it is,
        // ListingEnums will gracefully handle it and just emit what was
        // passed in. However, if we ever got a raw enum value for whatever
        // reason, we'd want to show the human-readable form.
        // This is probably overkill, but it is what we've been doing for
        // a while, and this will eventually be going away once subcategory
        // filtering (`category`) is ready to be enabled for production and
        // won't need to be turned back off.
        value={option as ItemCategoriesDtoCategoriesEnum}
      />
    )
  },
  condition: (option: string) => {
    // Search Service gives us already transformed condition names.
    // For additional details, see `transformCondition` in `components/listingCard/BaseListingCard/ListingInfo/InventoryTypeAndCondition.tsx`
    // Everything that applies to the result data applies to filters as well
    // (at least as of July 1, 2025)
    if (option === 'Brand New') {
      return 'New'
    }
    return option
  },
  inventoryType: (option: string) => (
    <SearchEnums.InventoryType.Component
      // We know the option will be an enum value in practice, even though
      // the API doesn't strictly enforce it. If it ever isn't, ListingEnums
      // should gracefully handle this and just output the raw option string.
      value={option as InventoryTypeEnum}
    />
  ),
  listingType: (option: string) => (
    <ListingEnums.ListingType.Component
      // We know the option will be an enum value in practice, even though
      // the API doesn't strictly enforce it. If it ever isn't, ListingEnums
      // should gracefully handle this and just output the raw option string.
      value={option as ListingDtoListingTypeEnum}
    />
  ),
  storefront: (name: string) => {
    // filter out item id and in some cases integ id 'i-' from string for the display name
    // '6647b7b6984775b0ce58c873-A Micro Center LLC' will return ['6647b7b6984775b0ce58c873-A Micro Center LLC', 'A Micro Center LLC']
    // 'ach-i-acehardware (Integ)' will return ['ach-i-acehardware (Integ)', 'acehardware (Integ)']
    const regex = /^[^-]+-(?:i-)?(.+)$/
    const match = name.match(regex)

    return <>{match ? match[1] : name}</>
  },
  // Unlike `storefront`, this is already a clean id — no composite string to parse apart.
  storeFrontId: (option: string) => option,
  transportMode: (option: string) => (
    <ListingEnums.TransportMode.Component
      // We know the option will be an enum value in practice, even though
      // the API doesn't strictly enforce it. If it ever isn't, ListingEnums
      // should gracefully handle this and just output the raw option string.
      value={option as ShippingTransportModeEnum}
    />
  ),

  // Special cases
  distanceRange: (distanceRange: ListingFiltersState['distanceRange']) => {
    const distanceUnit = 'miles'

    return (
      <span>
        {distanceRange.distance} {distanceUnit} from {distanceRange.zipCode}
      </span>
    )
  },
  warehouseLocation: (value: string) => {
    // Warehouse locations are in the format of `COUNTRY_CODE/PROVINCE_CODE/City`
    // What we want to show is "City, STATE_CODE" for US, and "City, COUNTRY_CODE"
    // for non-US (similar to what we do for showing the Location details in the
    // listing cards).
    const split = value.split('/')
    const city = split.at(-1)
    const country = split.at(0)
    const region = country === 'US' ? split.at(-2) : country
    const display = [city]
    if (region && region !== city) {
      display.push(region)
    }
    return display.join(', ')
  },
} satisfies Record<
  // All string properties, except for region (a more complex special case) should have
  // a lookup in here... otherwise Saved Search will show incorrectly
  Exclude<KeysMatching<ListingFiltersState, string[]>, 'region'>,
  (option: string) => React.ReactNode
> & {
  distanceRange: (
    option: ListingFiltersState['distanceRange']
  ) => React.ReactNode
}
